import { useState } from "react";
import { Calendar, ArrowUpRight, Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/tracking";

const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || "";

/**
 * Renders a Google Workspace Calendar appointment scheduling embed
 * with a premium dark frame, gradient border glow, and a branded loading state.
 *
 * Variants:
 *   - "card"   (default): full embed with header chrome + iframe
 *   - "button": pill button that opens the booking page in a new tab
 */
export default function BookingEmbed({ variant = "card" }) {
  const [loaded, setLoaded] = useState(false);

  if (!BOOKING_URL) {
    return (
      <div
        data-testid="booking-fallback"
        className="bg-white border border-[#E5E7EB] rounded-2xl p-7"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#6D4DFF]/12 to-[#00D4FF]/12 grid place-items-center text-[#6D4DFF]">
            <Calendar className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-[#07111F]">
              Book a working session
            </h3>
            <p className="mt-1.5 text-sm text-[#6B7280] leading-relaxed">
              Calendar booking will appear here once the workspace calendar URL is configured.
              For now, drop us a note via the form and we&apos;ll send slots within one business day.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const url = BOOKING_URL.includes("gv=true")
    ? BOOKING_URL
    : `${BOOKING_URL}${BOOKING_URL.includes("?") ? "&" : "?"}gv=true`;

  if (variant === "button") {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="booking-button"
        onClick={() => trackEvent("book_call_click", { id: "booking_button" })}
        className="xa-btn xa-btn-primary"
      >
        <Calendar className="w-4 h-4" /> Book a meeting <ArrowUpRight className="w-4 h-4" />
      </a>
    );
  }

  return (
    <div
      data-testid="booking-embed"
      className="relative group"
    >
      {/* Brand gradient glow */}
      <div
        aria-hidden
        className="absolute -inset-px rounded-[20px] bg-gradient-to-br from-[#6D4DFF] via-[#6D4DFF]/40 to-[#00D4FF] opacity-60 blur-[1px] group-hover:opacity-80 transition-opacity pointer-events-none"
      />

      <div className="relative bg-[#07111F] border border-white/10 rounded-2xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(7,17,31,0.5)]">
        {/* Header */}
        <div className="relative flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-white/10 bg-gradient-to-r from-[#07111F] via-[#0B1A2E] to-[#07111F] gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-[#6D4DFF] to-[#00D4FF] grid place-items-center text-white shadow-[0_8px_24px_-8px_rgba(109,77,255,0.6)] shrink-0">
              <Calendar className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-semibold text-white text-sm leading-tight truncate">
                Pick a 30-min slot
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#00D4FF] mt-0.5 truncate">
                Live · Google Workspace Calendar
              </div>
            </div>
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="booking-open-new-tab"
            className="hidden sm:inline-flex text-xs font-medium text-white/70 hover:text-white items-center gap-1.5 px-3 py-1.5 border border-white/15 rounded-full hover:border-white/30 transition shrink-0"
          >
            Open in new tab <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* Iframe wrapper */}
        <div className="relative bg-white" style={{ minHeight: 720 }}>
          {/* Loading shimmer */}
          {!loaded && (
            <div
              data-testid="booking-loading"
              className="absolute inset-0 bg-white flex flex-col items-center justify-center gap-4 z-10 px-6"
            >
              <div className="relative w-12 h-12">
                <div className="absolute inset-0 rounded-full border-2 border-[#E5E7EB]" />
                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#6D4DFF] border-r-[#00D4FF] animate-spin" />
                <Loader2 className="absolute inset-0 m-auto w-5 h-5 text-[#6D4DFF] opacity-0" />
              </div>
              <div className="text-center">
                <div className="font-display text-sm font-semibold text-[#07111F]">
                  Loading available slots…
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280] mt-1.5">
                  Syncing with Google Calendar
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2 w-full max-w-md">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-9 rounded bg-gradient-to-br from-[#F3F4F6] to-[#E5E7EB] animate-pulse"
                    style={{ animationDelay: `${i * 80}ms` }}
                  />
                ))}
              </div>
            </div>
          )}
          <iframe
            src={url}
            title="Book a meeting with Xadentra"
            className={`w-full block transition-opacity duration-500 h-[720px] sm:h-[720px] lg:h-[760px] ${loaded ? "opacity-100" : "opacity-0"}`}
            style={{ border: 0, maxWidth: "100%", width: "100%" }}
            loading="lazy"
            scrolling="yes"
            onLoad={() => {
              setLoaded(true);
              trackEvent("booking_iframe_loaded", {});
            }}
          />
        </div>
      </div>
    </div>
  );
}
